import React, { useEffect, useMemo, useState } from 'react';
import { Text, Typography } from '@fiverr-private/typography';
import { atoms } from '@fiverr-private/theme';
import { Stack, Wrap } from '@fiverr-private/layout_components';
import { ChoiceGroup, ChoiceGroupItem, useForm, z, zodResolver } from '@fiverr-private/forms';
import { ClockIcon, TagIcon } from '@fiverr-private/visuals';
import { Gql } from '@fiverr-private/dynamic_matching';
import { AppI18n, appTranslate } from '../../../i18n';
import { useBriefCreationContext } from '../../../context/BriefCreationContext';
import { EditComponentProps, TitleComponentProps, ViewComponentProps } from '..';
import { Budget, BudgetSchema } from '../../../api';
import { EditActions } from '../EditActions';
import { Currency } from '../../../types/currency';
import { centsToCurrency, currencyToCents } from './formatCurrency';
import { BudgetInput } from './BudgetInput';
import { BudgetRecommendedEducation } from './BudgetRecommendedEducation';

export interface BudgetSectionContent {
  budgetInCents: number;
  pricingType: Gql.BriefPricingType | null;
}

export const BudgetTitleComponent = ({ isEditing, color }: TitleComponentProps) => (
  <Typography
    fontWeight="bold"
    className={atoms({
      whiteSpace: 'preWrap',
    })}
    marginBottom={isEditing ? '3' : '0'}
    {...(color && { color })}
  >
    <AppI18n k={`sections.budget.${isEditing ? 'edit_' : ''}title`} />
  </Typography>
);

export const BudgetViewComponent = ({ section }: ViewComponentProps) => {
  const { currency } = useBriefCreationContext();
  const content: BudgetSectionContent = (section.content || {}) as unknown as BudgetSectionContent;
  const selectedBudget = {
    budgetInCents: content.budgetInCents || 0,
    pricingType: content.pricingType || Gql.BriefPricingType.Fixed,
  };

  return (
    <Text>
      <AppI18n k="sections.budget.up_to" />
      &nbsp;
      {centsToCurrency(selectedBudget.budgetInCents, currency)}
      {selectedBudget.pricingType === Gql.BriefPricingType.Hourly && (
        <>
          /
          <AppI18n k="sections.budget.hour" />
        </>
      )}
    </Text>
  );
};

export const getBudgetAsNumber = (value: string) => (!value ? 0 : parseFloat(value.replace(/,/g, '')));

const getBudgetInCents = (value: string, currency: Currency) => {
  const valueAsNumber = getBudgetAsNumber(value);

  return currencyToCents(valueAsNumber, currency.rate);
};

export const BudgetEditComponent = ({ section, onChange, onEditEnd, onCancel }: EditComponentProps) => {
  const content: BudgetSectionContent = (section.content || {}) as any;
  const [sectionContentState, setSectionContentState] = useState<BudgetSectionContent>({
    budgetInCents: content.budgetInCents || 0,
    pricingType: content.pricingType || Gql.BriefPricingType.Fixed,
  });
  const { briefClassification, currency } = useBriefCreationContext();

  const {
    budget: fixedBudget,
    hourly_budget: hourlyBudget,
    is_hourly_eligible: isHourlyEligible,
  } = briefClassification!.static_data!;

  const isFixedBudgetMode: boolean = sectionContentState.pricingType === Gql.BriefPricingType.Fixed;

  const budget: Budget = isFixedBudgetMode ? BudgetSchema.parse(fixedBudget) : BudgetSchema.parse(hourlyBudget);

  const { min: recommendedMinBudget, validMin: validMinBudget, validMax: validMaxBudget } = budget;

  useEffect(() => {
    const run = async () => {
      const budgetValue = sectionContentState.budgetInCents ? (sectionContentState.budgetInCents / 100).toString() : '';

      methods.reset({ budget: budgetValue });
      methods.clearErrors();
      await methods.trigger();
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionContentState.pricingType]);

  const schema = useMemo(
    () =>
      z.object({
        budget: z
          .string()
          .refine(
            (value) =>
              value === '' ||
              (getBudgetInCents(value, currency) >= validMinBudget &&
                getBudgetInCents(value, currency) <= validMaxBudget),
            {
              message: appTranslate(
                `sections.budget.${isFixedBudgetMode ? 'invalid_fixed_rate' : 'invalid_hourly_rate'}`,
                {
                  params: {
                    min: centsToCurrency(validMinBudget, currency),
                    max: centsToCurrency(validMaxBudget, currency),
                  },
                }
              ).toString(),
            }
          ),
      }),
    [isFixedBudgetMode, validMinBudget, validMaxBudget, currency]
  );

  const methods = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const validateAndChange = async (updatedValue: BudgetSectionContent) => {
    if (await methods.trigger('budget')) {
      onChange(updatedValue as any);
    }
  };

  const handlePricingTypeChange = async (pricingType: Gql.BriefPricingType | null) => {
    if (!pricingType) return;

    const updatedSectionContentState = {
      ...sectionContentState,
      pricingType,
    };

    setSectionContentState(updatedSectionContentState);
    methods.clearErrors('budget');

    onChange(updatedSectionContentState as any);
  };

  const isDisabled = () => !methods.getValues('budget') || !!methods.formState.errors.budget;

  return (
    <Stack direction="column" gap="4">
      {isHourlyEligible && (
        <ChoiceGroup value={sectionContentState.pricingType} onChange={handlePricingTypeChange}>
          {Object.keys(Gql.BriefPricingType).map((key) => (
            <ChoiceGroupItem key={key} value={Gql.BriefPricingType[key]}>
              <Wrap alignItems="center" gap="1.5">
                {Gql.BriefPricingType[key] === Gql.BriefPricingType.Fixed ? (
                  <TagIcon color="grey_1200" />
                ) : (
                  <ClockIcon color="grey_1200" />
                )}
                <Text fontWeight="semibold" size="b_sm">
                  <AppI18n k={`sections.budget.${Gql.BriefPricingType[key].toLowerCase() as 'hourly' | 'fixed'}`} />
                </Text>
              </Wrap>
            </ChoiceGroupItem>
          ))}
        </ChoiceGroup>
      )}
      <Stack direction="column" gap="2">
        <BudgetInput
          validateAndChange={validateAndChange}
          methods={methods}
          setSectionContentState={setSectionContentState}
          sectionContentState={sectionContentState}
          isHourlyEligible={isHourlyEligible}
        />
        {recommendedMinBudget &&
          sectionContentState.budgetInCents < recommendedMinBudget &&
          sectionContentState.budgetInCents >= validMinBudget &&
          isFixedBudgetMode && <BudgetRecommendedEducation minimumPrice={recommendedMinBudget} currency={currency} />}
      </Stack>
      <EditActions onCancel={onCancel} onEditEnd={onEditEnd} isSaveDisabled={isDisabled()} />
    </Stack>
  );
};
